import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar
 * @property {HTMLButtonElement} addToCartButton
 */

class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton'];

  /** @type {IntersectionObserver | null} */
  #buttonObserver = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  connectedCallback() {
    super.connectedCallback();
    this.#observeMainButton();

    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section');
    section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartComplete, { signal });

    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.refs.stickyBar.dataset.stuck === 'true' && this.#isChatActive()) this.#hide();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buttonObserver?.disconnect();
    this.#abortController.abort();
  }

  #observeMainButton() {
    this.#buttonObserver?.disconnect();
    const productForm = this.#getProductForm();
    this.#targetAddToCartButton = productForm?.querySelector('[ref="addToCartButton"]') ?? null;
    if (!this.#targetAddToCartButton) return;

    this.#buttonObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      if (entry.isIntersecting) {
        this.#hide();
        return;
      }

      const buttonRect = entry.target.getBoundingClientRect();
      if (buttonRect.bottom <= 0 && !this.#isChatActive()) {
        this.#show();
      } else {
        this.#hide();
      }
    }, { threshold: 0 });

    this.#buttonObserver.observe(this.#targetAddToCartButton);
  }

  handleAddToCartClick = () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
  };

  /** @param {ProductSelectEvent} event */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        if (detail.productId && detail.productId !== this.dataset.productId) return;

        const replacement = detail.html.querySelector('sticky-add-to-cart');
        const available = replacement?.dataset.variantAvailable === 'true';
        this.dataset.variantAvailable = available ? 'true' : 'false';
        this.refs.addToCartButton.disabled = !available;
        requestAnimationFrame(() => this.#observeMainButton());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /** @param {CartLinesUpdateEvent | CartErrorEvent} event */
  #handleCartComplete = (event) => {
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) this.#targetAddToCartButton.dataset.puppet = 'false';
    };

    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  #show() {
    this.refs.stickyBar.dataset.stuck = 'true';
  }

  #hide() {
    this.refs.stickyBar.dataset.stuck = 'false';
  }

  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  #getProductForm() {
    const productId = this.dataset.productId;
    const section = this.closest('.shopify-section');
    if (!productId || !section) return null;

    const sectionId = section.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
